// Script Block editor (SCR-4, ARCH §21). The debuggable command-block successor's
// GUI: a source editor with a live compile-status line (positioned errors surfaced,
// never a crash), a "Run once" test trigger, and an Autopilot preset that loads the
// AVI-3 controller with editable Kp/Ki/Kd + target altitude. Client-only (ENG-1);
// the language + controller it drives are pure and deterministic.
import { h } from '../h';
import { tryCompile } from '../../../sim/script';
import type { ScriptDiagnostics } from '../../../sim/script/system';
import { type AutopilotPreset, DEFAULT_AUTOPILOT_PRESET } from '../../../sim/aviation/autopilot';
import type { Game } from '../../boot';
import { needsMergePrompt, scriptDiffPrompt } from './scriptdiff';

const SAMPLE = 'on redstone {\n  setblock(self.x, self.y + 1, self.z, "glowstone")\n}';

// ── §27.16 diagnostics: the four read-outs, as TEXT ──────────────────────────
// Formatting is a pure function of the diagnostics so it can be tested without a DOM
// (`tests/script_diagnostics.test.ts`); the render below only turns these strings into
// elements. The interesting case is E-44's: `installed` AND `compileError` both set
// means the last-good program is still running and the refused edit sits beside it —
// an author staring at a working sheep and a red line needs to be told that in words.
export interface DiagnosticsView {
  /** The op read-out against the budget (§27.16), e.g. `48 / 512 ops`. */
  ops: string;
  /** What is running right now, in the author's terms. */
  state: string;
  /** The error ring, oldest first, already rendered `tick … line … message`. */
  errors: string[];
  /** The last dispatch's effects, in the order the script asked for them. */
  effects: string[];
}

export function diagnosticsView(diag: ScriptDiagnostics | null): DiagnosticsView {
  if (!diag) return { ops: '—', state: 'no script installed', errors: [], effects: [] };
  return {
    ops: `${diag.lastOps} / ${diag.opBudget} ops`,
    state: describeState(diag),
    // Oldest first, exactly as the ring stores it — an author reads their edit history
    // forwards, and reversing it here would disagree with `SCR.ERROR_RING`'s eviction.
    errors: diag.errors.map((e) => (
      e.line === undefined ? `tick ${e.tick}: ${e.message}` : `tick ${e.tick}, line ${e.line}: ${e.message}`
    )),
    effects: diag.lastEffects.map((e) => `${e.verb}(${e.args.map((a) => String(a)).join(', ')})`),
  };
}

function describeState(diag: ScriptDiagnostics): string {
  // E-44 first, because it is the state that looks like a contradiction: a red error
  // and a mob that still behaves. Saying "last-good still running" is the whole point.
  if (diag.installed && diag.compileError) return `last-good program still running — the edit was refused: ${diag.compileError.message}`;
  if (diag.compileError) return `nothing running — refused: ${diag.compileError.message}`;
  if (diag.empty) return 'nothing to run (blank source)';
  if (!diag.installed) return 'no program installed';
  return `running · ${diag.dispatches} dispatch${diag.dispatches === 1 ? '' : 'es'}`;
}

// ── "Load example" (ARCH §27.16/§27.17) ──────────────────────────────────────
// The tutorial's worked examples, read from the opt-in `examples` collection —
// the same files `tests/script_examples.test.ts` compiles and the help screen's
// Scripting section quotes, so the picker cannot drift from either.
//
// This widget is the BLOCK editor, and four of the five examples are MOB scripts
// (§27.5's other host slice). They are still offered — reading one is how an author
// learns what a mob script looks like — but loading one says so plainly instead of
// letting the live compile report "not available to a block script" as if the
// example were broken.
const EXAMPLE_SOURCES = import.meta.glob('/data/collections/examples/*.tfs', {
  eager: true, query: '?raw', import: 'default',
}) as Record<string, string>;

interface Example {
  name: string;
  host: 'block' | 'mob';
  teaches: string;
}

const EXAMPLES: readonly Example[] = [
  { name: 'door_opener', host: 'block', teaches: 'a redstone-driven doorway — a block script, like this one' },
  { name: 'skittish', host: 'mob', teaches: 'fall-through: handled() skips the archetype for one dispatch' },
  { name: 'patrol', host: 'mob', teaches: 'a memory-driven state machine over a list of waypoints' },
  { name: 'guard', host: 'mob', teaches: 'a fn, a home point kept in memory, and an else-if ladder' },
  { name: 'greeter', host: 'mob', teaches: 'on interact, chat, and seeded randomness' },
];

const exampleSource = (name: string): string => EXAMPLE_SOURCES[`/data/collections/examples/${name}.tfs`] ?? '';

export function openScript(game: Game, x: number, y: number, z: number): void {
  const data = game.scriptAt(x, y, z);
  if (!data) return;

  game.uiOpen = true;
  document.exitPointerLock();

  const overlay = h('div', { class: 'bb-window-overlay' });
  const panel = h('div', { class: 'bb-window bb-script', 'data-testid': 'script-screen' });
  overlay.append(panel);
  document.body.append(overlay);

  const status = h('div', { class: 'bb-script-status', 'data-testid': 'script-status' });
  const setStatus = (text: string, ok: boolean): void => {
    status.textContent = text;
    status.classList.toggle('bb-script-ok', ok);
    status.classList.toggle('bb-script-err', !ok);
  };

  const source = h('textarea', {
    class: 'bb-input bb-script-source', 'data-testid': 'script-source',
    spellcheck: false, rows: 12, placeholder: SAMPLE,
  }) as HTMLTextAreaElement;
  source.value = data.source;

  // Live compile check as you type (compile errors are shown, not thrown).
  const check = (): void => {
    const src = source.value.trim();
    if (src === '') { setStatus('empty', true); return; }
    const c = tryCompile(src);
    if (c.ok) setStatus('✓ compiles', true);
    else setStatus(c.error.message, false);
  };
  source.oninput = check;

  // ── §27.16's diagnostics panel ──
  // Refreshed after the two actions that can change it — a save (which re-installs, so
  // a refusal lands in the ring) and a "Run once" (which produces a fresh op count and
  // effects list). It is deliberately NOT polled: a read-out that changes while nobody
  // did anything is harder to reason about than one that changes when you act.
  const diagnostics = h('div', { class: 'bb-script-diag', 'data-testid': 'script-diag' });
  const refreshDiagnostics = (): void => {
    const view = diagnosticsView(game.scriptDiagnostics(x, y, z));
    diagnostics.replaceChildren(
      h('div', { class: 'bb-label' }, 'Diagnostics'),
      h('div', { class: 'bb-script-diag-ops', 'data-testid': 'script-diag-ops' }, view.ops),
      h('div', { class: 'bb-script-diag-state', 'data-testid': 'script-diag-state' }, view.state),
      h('div', { class: 'bb-label' }, `Errors (${view.errors.length})`),
      h('div', { class: 'bb-script-diag-errors', 'data-testid': 'script-diag-errors' },
        ...(view.errors.length === 0
          ? [h('div', {}, 'none')]
          : view.errors.map((line) => h('div', {}, line)))),
      h('div', { class: 'bb-label' }, 'Last dispatch'),
      h('div', { class: 'bb-script-diag-effects', 'data-testid': 'script-diag-effects' },
        ...(view.effects.length === 0
          ? [h('div', {}, 'no effects')]
          : view.effects.map((line) => h('div', {}, line)))),
    );
  };

  // ── "Load example" picker (§27.16) ──
  const examplePick = h('select', {
    class: 'bb-select bb-script-example', 'data-testid': 'script-example',
  }, h('option', { value: '' }, 'Load example…'),
  ...EXAMPLES.map((e) => h('option', { value: e.name }, `${e.name}.tfs — ${e.teaches}`)),
  ) as HTMLSelectElement;
  // §27.15 (M47.5): loading a collection's script over text the author wrote is the one
  // place this editor can silently overwrite their work, so it asks first — with a
  // line-level diff and keep-mine / take-theirs / merge-by-hand. It does NOT ask when
  // there is nothing to lose: an empty buffer, the same text, or another example the
  // author loaded and never touched.
  const diffSlot = h('div', { class: 'bb-script-diffslot' });
  const afterLoad = (picked: Example, pristine: boolean): void => {
    if (picked.host === 'block' || !pristine) { check(); return; }
    // A mob example in a block editor: explain the slice rather than report an error.
    setStatus(`${picked.name}.tfs is a MOB script — self.*, mem.* and handled() belong to an entity, so a block cannot run it. Read it here, then paste it into Content editor ▸ Mobs ▸ Script.`, true);
  };
  examplePick.onchange = () => {
    const picked = EXAMPLES.find((e) => e.name === examplePick.value);
    if (!picked) return;
    const theirs = exampleSource(picked.name);
    const untouched = EXAMPLES.map((e) => exampleSource(e.name));
    if (!needsMergePrompt(source.value, theirs, untouched)) {
      diffSlot.replaceChildren();
      source.value = theirs;
      afterLoad(picked, true);
      return;
    }
    diffSlot.replaceChildren(scriptDiffPrompt({
      title: `Load ${picked.name}.tfs over your edit?`,
      mine: source.value,
      theirs,
      onResolve: (text) => {
        diffSlot.replaceChildren();
        source.value = text;
        afterLoad(picked, text === theirs);
      },
    }));
  };

  // ── Save + test-run ──
  const saveBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'script-save' }, 'Save') as HTMLButtonElement;
  saveBtn.onclick = () => {
    const r = game.setScriptSource(x, y, z, source.value);
    setStatus(r.ok ? '✓ saved' : r.error ?? 'error', r.ok);
    refreshDiagnostics(); // a refused save lands in the ring; E-44 keeps the last good one
  };
  const runBtn = h('button', { class: 'bb-btn', 'data-testid': 'script-run' }, 'Run once') as HTMLButtonElement;
  runBtn.onclick = () => {
    game.setScriptSource(x, y, z, source.value); // save first so the run uses the edited text
    const r = game.runScriptBlock(x, y, z, 'redstone');
    setStatus(r.message, r.ok);
    refreshDiagnostics(); // a fresh op count and a fresh effects list
  };

  // Hangar §39.5 (M105.3) — `.tfscript` through the Hangar pickers (or a download,
  // unbridged). The file is the raw source text, same as the textarea holds.
  const exportBtn = h('button', { class: 'bb-btn', 'data-testid': 'script-export' }, 'Export') as HTMLButtonElement;
  exportBtn.onclick = () => {
    void (async () => {
      const { exportFile } = await import('../../hangar/files');
      await exportFile(game.hangar, new TextEncoder().encode(source.value), 'script.tfscript', ['.tfscript'], 'text/plain');
      setStatus('exported', true);
    })();
  };
  const importFileBtn = h('button', { class: 'bb-btn', 'data-testid': 'script-import-file' }, 'Import file…') as HTMLButtonElement;
  importFileBtn.onclick = () => {
    void (async () => {
      const { importFile } = await import('../../hangar/files');
      const bytes = await importFile(game.hangar, ['.tfscript']);
      if (!bytes) return;
      source.value = new TextDecoder().decode(bytes);
      check();
    })();
  };

  // ── Autopilot preset (AVI-3): editable target altitude + PID gains ──
  const preset: AutopilotPreset = { ...(data.autopilot ?? DEFAULT_AUTOPILOT_PRESET) };
  const numRow = (label: string, testid: string, step: number, get: () => number, set: (v: number) => void): HTMLElement => {
    const input = h('input', { class: 'bb-input bb-script-num', type: 'number', 'data-testid': testid, step: String(step), value: String(get()) }) as HTMLInputElement;
    input.oninput = () => { const v = Number(input.value); if (Number.isFinite(v)) set(v); };
    return h('label', { class: 'bb-script-field' }, h('span', {}, label), input);
  };
  const alt = numRow('Altitude', 'script-alt', 1, () => preset.altitude, (v) => { preset.altitude = v; });
  const kp = numRow('Kp', 'script-kp', 0.001, () => preset.kp, (v) => { preset.kp = v; });
  const ki = numRow('Ki', 'script-ki', 0.001, () => preset.ki, (v) => { preset.ki = v; });
  const kd = numRow('Kd', 'script-kd', 0.001, () => preset.kd, (v) => { preset.kd = v; });

  const loadPresetBtn = h('button', { class: 'bb-btn', 'data-testid': 'script-preset' }, 'Load autopilot preset') as HTMLButtonElement;
  loadPresetBtn.onclick = () => {
    Object.assign(preset, DEFAULT_AUTOPILOT_PRESET);
    for (const el of panel.querySelectorAll<HTMLInputElement>('.bb-script-num')) el.dispatchEvent(new Event('input'));
    (panel.querySelector('[data-testid="script-alt"]') as HTMLInputElement).value = String(preset.altitude);
    (panel.querySelector('[data-testid="script-kp"]') as HTMLInputElement).value = String(preset.kp);
    (panel.querySelector('[data-testid="script-ki"]') as HTMLInputElement).value = String(preset.ki);
    (panel.querySelector('[data-testid="script-kd"]') as HTMLInputElement).value = String(preset.kd);
    setStatus('autopilot preset loaded — tune the gains, then Engage', true);
  };
  const engageBtn = h('button', { class: 'bb-btn bb-primary', 'data-testid': 'script-engage' }, 'Engage autopilot') as HTMLButtonElement;
  engageBtn.onclick = () => {
    game.setScriptAutopilot(x, y, z, { ...preset });
    const flew = game.engageScriptAutopilot(x, y, z);
    setStatus(flew ? `✓ autopilot engaged → hold ${Math.round(preset.altitude)}m` : 'preset saved (engages when flying)', true);
  };

  let closed = false;
  const close = (): void => {
    if (closed) return;
    closed = true;
    document.removeEventListener('keydown', onKey, true);
    overlay.remove();
    game.uiOpen = false;
    void game.renderer.domElement.requestPointerLock();
  };
  const onKey = (e: KeyboardEvent): void => {
    if (document.activeElement === source) { if (e.code === 'Escape') source.blur(); return; }
    if (e.code === 'Escape') { e.preventDefault(); e.stopPropagation(); close(); }
  };
  document.addEventListener('keydown', onKey, true);
  overlay.addEventListener('mousedown', (e) => { if (e.target === overlay) close(); });

  panel.append(
    h('div', { class: 'bb-window-title' }, 'Script Block'),
    h('div', { class: 'bb-script-body' },
      h('div', { class: 'bb-label' }, 'Script'),
      examplePick,
      diffSlot,
      source,
      status,
      h('div', { class: 'bb-script-actions' }, saveBtn, runBtn, exportBtn, importFileBtn),
      diagnostics,
      h('div', { class: 'bb-script-preset' },
        h('div', { class: 'bb-label' }, 'Autopilot preset (AVI-3)'),
        h('div', { class: 'bb-script-fields' }, alt, kp, ki, kd),
        h('div', { class: 'bb-script-actions' }, loadPresetBtn, engageBtn),
      ),
    ),
    h('div', { class: 'bb-script-actions' }, h('button', { class: 'bb-btn', onclick: close }, 'Close')),
  );

  check();
  refreshDiagnostics();
}
